const MAX_MESSAGE_LENGTH = 3000;
const MIN_POST_INTERVAL_MS = 1500;
const DEDUPE_WINDOW_MS = 15000;

let _isPatched = false;
let _lastPostedAt = 0;
let _dedupeCache = new Map();
let _getState = null;
let _platform = 'web';
let _isElectronRuntime = null;
let _originalConsoleWarn;
let _originalConsoleError;
let _runtimeConsole;

/**
 * Stores Redux state accessor and runtime platform for Slack reporting.
 *
 * @param {Function} getState - Redux getState function.
 * @param {'web' | 'native'} platform - Runtime platform.
 * @returns {void}
 */
export function configureSlackLoggingContext(getState, platform) {
    if (typeof getState !== 'function') {
        return;
    }

    _getState = getState;
    _platform = platform;
}

/**
 * Installs production-only console warn/error forwarding to Slack.
 *
 * @param {Function} getState - Redux getState function.
 * @param {'web' | 'native'} platform - Runtime platform.
 * @returns {void}
 */
export function initSlackConsoleLogging(getState, platform) {
    configureSlackLoggingContext(getState, platform);

    if (_isPatched) {
        return;
    }

    _runtimeConsole = globalThis.console;

    if (!_runtimeConsole) {
        return;
    }

    _isPatched = true;

    _originalConsoleWarn = _runtimeConsole.warn;
    _originalConsoleError = _runtimeConsole.error;

    _runtimeConsole.warn = (...args) => {
        _originalConsoleWarn.apply(_runtimeConsole, args);
        reportLogToSlack('warn', args);
    };

    _runtimeConsole.error = (...args) => {
        _originalConsoleError.apply(_runtimeConsole, args);
        reportLogToSlack('error', args);
    };
}

/**
 * Sends warn/error payload to Slack using current logging context.
 *
 * @param {'warn' | 'error'} level - Log level.
 * @param {Array<any>} args - Log args.
 * @returns {void}
 */
export function reportLogToSlack(level, args) {
    if (typeof _getState !== 'function') {
        return;
    }

    _postToSlack(level, args, _getState, _platform);
}

/**
 * Posts a structured log message to Slack.
 *
 * @param {'warn' | 'error'} level - Log level.
 * @param {Array<any>} args - Console arguments.
 * @param {Function} getState - Redux getState function.
 * @param {'web' | 'native'} platform - Runtime platform.
 * @returns {void}
 */
function _postToSlack(level, args, getState, platform) {
    try {
        let state;

        try {
            state = getState();
        } catch (error) {
            return;
        }

        const config = state?.['features/base/config'] || {};
        const slackLogging = config?.slackLogging || {};

        if (!_isSlackLoggingEnabled(slackLogging)) {
            return;
        }

        if (!_shouldSendLevel(slackLogging, level)) {
            return;
        }

        const webhookUrl = _resolveWebhookUrl(slackLogging);

        if (!webhookUrl) {
            return;
        }

        const now = Date.now();

        if (now - _lastPostedAt < (slackLogging.minIntervalMs || MIN_POST_INTERVAL_MS)) {
            return;
        }

        const message = _stringifyArgs(args).slice(0, slackLogging.maxMessageLength || MAX_MESSAGE_LENGTH);
        const dedupeKey = `${level}:${message}`;
        const previousTs = _dedupeCache.get(dedupeKey);

        if (previousTs && now - previousTs < DEDUPE_WINDOW_MS) {
            return;
        }

        _dedupeCache.set(dedupeKey, now);
        if (_dedupeCache.size > 200) {
            _dedupeCache = new Map(Array.from(_dedupeCache.entries()).slice(-100));
        }

        _lastPostedAt = now;

        const settings = state?.['features/base/settings'] || {};
        const conferenceState = state?.['features/base/conference'] || {};
        const roomName = conferenceState.room || 'unknown-room';
        const meetingId = _safeGetMeetingId(conferenceState);
        const email = settings.email || settings.userEmail || 'unknown-email';
        const header = `[${_resolveAppLabel(platform)}] ${level.toUpperCase()}`;
        const meetingContext = meetingId === roomName
            ? `meeting: ${meetingId}`
            : `room: ${roomName} | meetingId: ${meetingId}`;

        const payload = {
            text: `${header} | email: ${email} | ${meetingContext}\n${message}`
        };

        const requestInit = {
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST'
        };

        if (platform === 'web') {
            requestInit.mode = 'no-cors';
            requestInit.keepalive = true;
        }

        fetch(webhookUrl, requestInit).catch(_handleSlackPostFailure);
    } catch (error) {
        if (_originalConsoleError && _runtimeConsole) {
            _originalConsoleError.call(_runtimeConsole, 'Failed to post to Slack:', error);
        }
    }
}

/**
 * Swallows Slack post failures intentionally.
 *
 * @returns {void}
 */
function _handleSlackPostFailure() {
    return undefined;
}

/**
 * Returns the label identifying which app a report came from: 'Mobile', 'Desktop' or 'Web'.
 *
 * The desktop app runs the very same web bundle, so the runtime platform is 'web' for both and every report from
 * Electron used to arrive labelled 'Web'. That distinction matters when triaging: screen sharing, always-on-top,
 * PiP and deep links behave differently there, and those reports were indistinguishable from browser ones.
 *
 * @param {'web' | 'native'} platform - Runtime platform passed in at init.
 * @returns {string}
 */
function _resolveAppLabel(platform) {
    if (platform === 'native') {
        return 'Mobile';
    }

    return _isElectron() ? 'Desktop' : 'Web';
}

/**
 * Detects whether this web bundle is running inside the Meet Hour desktop app.
 *
 * Two independent signals, because neither covers everything on its own: the desktop shell injects
 * `window.MeetHourElectron` (the same global the desktop picker calls into), and the Electron user agent is what
 * lib-meet-hour's `browser.isElectron()` and index.html key off. The user agent also covers desktop builds that
 * inject the global later than this module runs.
 *
 * Deliberately dependency-free: this module is installed over `console` during bootstrap, before the feature
 * modules (including lib-meet-hour's browser detection) are guaranteed to be usable. The result is cached - it
 * cannot change within a runtime.
 *
 * @returns {boolean}
 */
function _isElectron() {
    if (typeof _isElectronRuntime === 'boolean') {
        return _isElectronRuntime;
    }

    try {
        _isElectronRuntime = typeof globalThis.MeetHourElectron !== 'undefined'
            || (typeof navigator !== 'undefined' && `${navigator.userAgent || ''}`.indexOf('Electron') !== -1);
    } catch (error) {
        _isElectronRuntime = false;
    }

    return _isElectronRuntime;
}

/**
 * Determines whether the provided level should be sent to Slack.
 *
 * Supported values:
 * - warn
 * - error
 * - both
 *
 * @param {Object} slackLogging - Slack logging config.
 * @param {'warn' | 'error'} level - Current log level.
 * @returns {boolean}
 */
function _shouldSendLevel(slackLogging, level) {
    const configuredLevel = String(slackLogging?.level || 'both').toLowerCase();

    switch (configuredLevel) {
    case 'warn':
        return level === 'warn';
    case 'error':
        return level === 'error';
    case 'both':
    default:
        return level === 'warn' || level === 'error';
    }
}

/**
 * Determines whether Slack logging is enabled for the current environment.
 *
 * Requires explicit `enabled: true` in config.
 *
 * @param {Object} slackLogging - Slack logging config.
 * @returns {boolean}
 */
function _isSlackLoggingEnabled(slackLogging) {
    if (process.env.NODE_ENV === 'development') {
        return false;
    }

    return slackLogging?.enabled === true;
}

/**
 * Resolves webhook URL from obfuscated or plain config value.
 *
 * @param {Object} slackLogging - Slack logging config.
 * @returns {string}
 */
function _resolveWebhookUrl(slackLogging) {
    const decoded = _decodeObfuscatedWebhookUrl(slackLogging?.webhookUrlObfuscated);
    const plain = typeof slackLogging?.webhookUrl === 'string'
        ? slackLogging.webhookUrl.trim()
        : '';
    const webhookUrl = decoded || plain;

    return webhookUrl.startsWith('https://hooks.slack.com/services/')
        ? webhookUrl
        : '';
}

/**
 * Decodes the obfuscated webhook URL.
 *
 * Obfuscation format:
 *  - Prefix: v1:
 *  - Payload: dash-separated integers
 *  - Rule: encodedCharCode = originalCharCode + ((index % 7) + 3)
 *
 * @param {string} obfuscated - Obfuscated webhook value from config.
 * @returns {string}
 */
function _decodeObfuscatedWebhookUrl(obfuscated) {
    if (typeof obfuscated !== 'string' || !obfuscated.startsWith('v1:')) {
        return '';
    }

    const encoded = obfuscated.slice(3).trim();

    if (!encoded) {
        return '';
    }

    const parts = encoded.split('-');
    const chars = [];

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();

        if (!part) {
            return '';
        }

        const num = Number(part);

        if (!Number.isInteger(num)) {
            return '';
        }

        chars.push(String.fromCharCode(num - ((i % 7) + 3)));
    }

    return chars.join('');
}

/**
 * Best-effort extraction of meeting id from conference state.
 *
 * @param {Object} conferenceState - features/base/conference state.
 * @returns {string}
 */
function _safeGetMeetingId(conferenceState) {
    try {
        const conference = conferenceState?.conference;

        if (conference && typeof conference.getMeetingUniqueId === 'function') {
            const id = conference.getMeetingUniqueId();

            if (id) {
                return String(id);
            }
        }
    } catch (error) {
        return conferenceState?.room || 'unknown-meeting';
    }

    return conferenceState?.room || 'unknown-meeting';
}

/**
 * Converts console arguments to a readable string.
 *
 * @param {Array<any>} args - Console args.
 * @returns {string}
 */
function _stringifyArgs(args) {
    if (!Array.isArray(args)) {
        return '';
    }

    return args.map(arg => {
        if (arg instanceof Error) {
            return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
        }

        if (typeof arg === 'string') {
            return arg;
        }

        if (typeof arg === 'number' || typeof arg === 'boolean' || arg === null || typeof arg === 'undefined') {
            return String(arg);
        }

        try {
            const seen = new WeakSet();

            return JSON.stringify(arg, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return '[Circular]';
                    }
                    seen.add(value);
                }

                if (typeof value === 'function') {
                    return `[Function ${value.name || 'anonymous'}]`;
                }

                return value;
            });
        } catch (error) {
            return '[Unserializable value]';
        }
    }).join(' ');
}
